import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Card, Button as BSButton } from 'reactstrap';
import cx from 'classnames';
import { ProductType } from '../../../../enums';
import { normalizeDescriptionItems } from '../../../../utils/descriptions';
export { Modal } from "./Modal";
export { Loading } from "./Loading";
export {default as CardPayment} from "./CardPayment";
export { MotorplayPlanCarousel } from './PlanCarousel';

export const PlanCard = ({
  plan: { name: title, recurrence, price, metadata, _id: id, currency, broadcastingDates, productType, event },
  selectPlan = () => null,
  selected,
  disabled,
  addProduct,
  products,
  cleanOrder,
  isDescriptionExpanded = false,
  onDescriptionExpandChange = () => null,
  onSelect,
  isBlocked = false,
}) => {
  const fireCleanOrder = () => {
    if (!cleanOrder) {
      return;
    }
    cleanOrder();
  }

  const onClick = () => {
    if (isBlocked || disabled) {
      return;
    }

    if (onSelect) {
      onSelect();
      return;
    }

    if(selected) {
      selectPlan('');
      fireCleanOrder();
      return;
    }
    fireCleanOrder();
    selectPlan(id);
    if(products?.length) {
      const selectedProductData = products.find((product) => product._id === id);
      addProduct({ ...selectedProductData });
    }
  }

  const descriptionItems = useMemo(
    () => normalizeDescriptionItems(metadata?.description),
    [metadata?.description]
  );
  const descriptionRef = useRef(null);
  const descriptionMeasureRef = useRef(null);
  const [showDescriptionToggle, setShowDescriptionToggle] = useState(false);

  const renderDescriptionContent = () => {
    if (descriptionItems.length > 1) {
      return (
        <ul className='description description--bulleted'>
          {descriptionItems.map((item, index) => (
            <li key={`${id}-${index}`} className='description__item'>
              <span className='description__item-text'>{item}</span>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p className='description description--single'>
        <span className='description__item-text'>{descriptionItems[0]}</span>
      </p>
    );
  };

  const updateDescriptionToggleVisibility = useCallback(() => {
    const el = descriptionRef.current;
    const measureEl = descriptionMeasureRef.current;

    if (!el || !measureEl || !descriptionItems.length) {
      setShowDescriptionToggle(false);
      return;
    }

    if (isDescriptionExpanded) {
      setShowDescriptionToggle(true);
      return;
    }

    setShowDescriptionToggle(measureEl.offsetHeight > el.offsetHeight + 1);
  }, [descriptionItems, isDescriptionExpanded]);

  useLayoutEffect(() => {
    updateDescriptionToggleVisibility();

    const el = descriptionRef.current;
    const measureEl = descriptionMeasureRef.current;
    if (!el || !measureEl || !descriptionItems.length) return;

    const resizeObserver = new ResizeObserver(updateDescriptionToggleVisibility);
    resizeObserver.observe(el);
    resizeObserver.observe(measureEl);

    let cancelled = false;
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) {
          updateDescriptionToggleVisibility();
        }
      });
    }

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
    };
  }, [descriptionItems, updateDescriptionToggleVisibility]);

  const handleDescriptionToggle = () => {
    onDescriptionExpandChange(!isDescriptionExpanded);
  };

  return (
    <Card className={`MembershipCard ${selected ? 'selected' : ''} ${isDescriptionExpanded ? 'MembershipCard--expanded' : ''} ${isBlocked ? 'MembershipCard--blocked' : ''}`}>
      <div className='MembershipCard__content'>
        <div className='MembershipCard__body'>
          <div className='MembershipCard__body__title'>
            <h5 className='MembershipTitle mt-2'>{title}</h5>
            <div className='amount mt-4'>
              <span className='price h3'>{price} {currency} {!event && '/'} </span>
              {!event && (
                <span className='recurrence'>
                  <FormattedMessage
                    id={`MembershipSelectionPage.recurrence.${recurrence}`}
                    defaultMessage={'mes'}
                  />
                </span>
              )}
            </div>
          </div>
          {descriptionItems.length > 0 && (
            <>
              <div className='description__wrapper'>
                <div
                  ref={descriptionRef}
                  className={`description__content ${isDescriptionExpanded ? 'description__content--expanded' : 'description__content--collapsed'}`}
                >
                  {renderDescriptionContent()}
                </div>
                <div
                  ref={descriptionMeasureRef}
                  aria-hidden='true'
                  className='description__content description__content--measure'
                >
                  {renderDescriptionContent()}
                </div>
              </div>
              {showDescriptionToggle && (
                <button
                  type='button'
                  className='description__toggle'
                  onClick={handleDescriptionToggle}
                >
                  <FormattedMessage
                    id={isDescriptionExpanded ? 'MembershipSelectionPage.description.showLess' : 'Caraousel.arrow.subtitle'}
                  />
                </button>
              )}
            </>
          )}
          <div className="broadcasting">
            {(broadcastingDates?.start || broadcastingDates?.end) && (
                <dl>
                  {broadcastingDates?.start && (
                    <>
                      <dt><FormattedMessage id="Carrousel.item.broadcasting.start"/></dt>
                      <dd>{new Date(broadcastingDates?.start).toLocaleString()}</dd>
                    </>
                  )}
                  {broadcastingDates?.end && (
                    <>
                      <dt><FormattedMessage id="Carrousel.item.broadcasting.end"/></dt>
                      <dd>{new Date(broadcastingDates?.end).toLocaleString()}</dd>
                    </>
                  )}
                </dl>
            )}
          </div>
        </div>
        <div className='MembershipCard__footer'>
          <BSButton
            disabled={disabled || isBlocked}
            className={`button-select ${selected && !disabled && !isBlocked ? 'selected' : ''}`}
            onClick={onClick}
          >
            {onSelect && selected ? (
              <FormattedMessage id='added' />
            ) : productType === ProductType.ADD_ON ? (
              <FormattedMessage id='add' />
            ) : (
              <FormattedMessage id='addPlan' />
            )}
          </BSButton>
        </div>
      </div>
    </Card>
  );
};
